import type {
  RingWorkflowView,
  WorkflowInput,
  WorkflowRunView,
  WorkflowSettingsView,
  WorkflowSubject,
  WorkflowTestLine,
  WorkflowView,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Workflows (WG-D1–WG-D19) and each person's ring order (QR11, WG-D19). Only an
 * administrator's writes go through (WG-D14); the server says so in a sentence.
 */

export function listWorkflows(signal?: AbortSignal): Promise<WorkflowView[]> {
  return api<WorkflowView[]>("/workflows", { signal });
}

export function getWorkflow(id: string, signal?: AbortSignal): Promise<WorkflowView> {
  return api<WorkflowView>(`/workflows/${id}`, { signal });
}

export function createWorkflow(body: WorkflowInput): Promise<WorkflowView> {
  return api<WorkflowView>("/workflows", { method: "POST", body });
}

export function saveWorkflow(id: string, body: WorkflowInput): Promise<WorkflowView> {
  return api<WorkflowView>(`/workflows/${id}`, { method: "PUT", body });
}

export function deleteWorkflow(id: string): Promise<void> {
  return api<void>(`/workflows/${id}`, { method: "DELETE" });
}

export function runWorkflowNow(id: string): Promise<{ started: number }> {
  return api<{ started: number }>(`/workflows/${id}/run-now`, { method: "POST", body: {} });
}

/** What it would do today, changing nothing. A draft is tested as it stands in the editor. */
export function testWorkflow(
  id: string | null,
  draft?: WorkflowInput,
): Promise<WorkflowTestLine[]> {
  return id === null || draft !== undefined
    ? api<WorkflowTestLine[]>("/workflows/test", { method: "POST", body: { draft } })
    : api<WorkflowTestLine[]>(`/workflows/${id}/test`, { method: "POST", body: {} });
}

export function workflowRuns(id: string, signal?: AbortSignal): Promise<WorkflowRunView[]> {
  return api<WorkflowRunView[]>(`/workflows/${id}/runs`, { signal });
}

export function workflowSettings(signal?: AbortSignal): Promise<WorkflowSettingsView> {
  return api<WorkflowSettingsView>("/workflows/settings", { signal });
}

export function pauseWorkflows(paused: boolean): Promise<WorkflowSettingsView> {
  return api<WorkflowSettingsView>("/workflows/settings", { method: "PUT", body: { paused } });
}

export function cancelWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return api<WorkflowRunView>(`/workflow-runs/${runId}/cancel`, { method: "POST", body: {} });
}

// ── The ring (WF-D3, WG-D18) ───────────────────────────────────────────────

export function ringWorkflows(
  subject: WorkflowSubject,
  signal?: AbortSignal,
): Promise<RingWorkflowView[]> {
  return api<RingWorkflowView[]>(`/workflows/ring?subject=${subject}`, { signal });
}

export function startRingRun(workflowId: string, subjectId: string): Promise<WorkflowRunView> {
  return api<WorkflowRunView>(`/workflows/${workflowId}/ring-run`, {
    method: "POST",
    body: { subjectId },
  });
}

export function answerRingRun(runId: string, answer: "done" | "skip"): Promise<WorkflowRunView> {
  return api<WorkflowRunView>(`/workflow-runs/${runId}/answer`, {
    method: "POST",
    body: { answer },
  });
}

// ── Each person's ring order (QR11, WG-D19) ────────────────────────────────

export function readRingOrders(signal?: AbortSignal): Promise<Record<string, string[]>> {
  return api<Record<string, string[]>>("/ring-orders", { signal });
}

export function saveRingOrder(ring: string, actions: string[]): Promise<void> {
  return api<void>(`/ring-orders/${encodeURIComponent(ring)}`, {
    method: "PUT",
    body: { actions },
  });
}

export function resetRingOrder(ring: string): Promise<void> {
  return api<void>(`/ring-orders/${encodeURIComponent(ring)}`, { method: "DELETE" });
}
